import React, {
  useRef,
  useLayoutEffect,
  useState,
  useMemo,
  useEffect,
} from "react";
import {
  config as baseConfig,
  lineConfig,
  barConfig,
  columnConfig,
  pieConfig,
  tableConfig,
} from "./config";
import Tooltip from "./SupportFiles/Tooltip";
import Select from "./SupportFiles/Select";
import twColors from "tailwindcss/colors";
import { useUniqueComponentId } from "./SupportFiles/uniqueId";
import {
  isGroupedSeries,
  normalizeForChart,
} from "./SupportFiles/dataNormalization";

//
import ColumnChartNew from "./CustomCharts/ColumnChartNew";
import BarChartNew from "./CustomCharts/BarChartNew";
import LineChartsNew from "./CustomCharts/LineChartsNew";
import PieChartNew from "./CustomCharts/PieChartNew";
import TableChartNew from "./CustomCharts/TableChartNew";
//
import FormFieldSet from "../FormFields/FormFieldSet";
import Button from "../Buttons/Button";
import DropdownMenuNew from "../Buttons/DropdownMenuNew";
import Divider from "../Common/Divider";
import Modal from "../Modal/Modal";

function readCssVar(name) {
  if (typeof window === "undefined") return "";
  const v = getComputedStyle(document.documentElement).getPropertyValue(name);
  return v ? v.trim() : "";
}
function resolveThemeToken(s) {
  if (!s || typeof s !== "string") return null;
  const m = s
    .trim()
    .match(
      /^(primary|neutrals|success|warning|danger|info|draft|leave|invoiced)(?:-([a-z0-9]+))?$/,
    );
  if (m) {
    const varName = m[2] ? `--color-${m[1]}-${m[2]}` : `--color-${m[1]}`;
    const v = readCssVar(varName);
    return v || `var(${varName})`;
  }
  if (s.startsWith("color-")) {
    const varName = `--${s}`;
    const v = readCssVar(varName);
    return v || `var(${varName})`;
  }
  return null;
}
function resolveTokenColor(t) {
  if (!t || typeof t !== "string") return t;
  const s = t.trim();
  if (s === "transparent") return "transparent";
  if (s === "black") return twColors.black;
  if (s === "white") return twColors.white;
  if (s.startsWith("#") || s.startsWith("rgb") || s.startsWith("hsl")) return s;
  if (s.startsWith("var(")) {
    const m = s.match(/var\((--[^)]+)\)/);
    const name = m && m[1];
    const v = name ? readCssVar(name) : "";
    return v || s;
  }
  const themeResolved = resolveThemeToken(s);
  if (themeResolved) return themeResolved;
  const [name, shade] = s.split("-");
  const p = twColors[name];
  if (p) {
    const v = shade ? p[shade] : p.DEFAULT || p["500"];
    if (v) return v;
  }
  return s;
}
function resolveColors(tokens) {
  if (!Array.isArray(tokens)) return [];
  return tokens.map(resolveTokenColor);
}

const baseToggleMenu = [
  { title: "Table Chart", icon: "table_chart_view", value: "table" },
  { title: "Bar Chart", icon: "bar_chart", value: "bar" },
  { title: "Column Chart", icon: "bar_chart", value: "column" },
  { title: "Pie Chart", icon: "donut_large", value: "pie" },
  { title: "Line Chart", icon: "line_axis", value: "line" },
];

const ChartsSetWrapperNew = ({
  title,
  type = "line", //
  data = [],
  categories = [],
  projection,
  options = {},
  padding,
  height = 360,
  width = "100%",
}) => {
  const componentId = useUniqueComponentId("charts-adv");
  const ref = useRef(null);
  const topSectionRef = useRef(null);
  const bottomLegendRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: height });
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [hidden, setHidden] = useState(new Set());
  const [hiddenGroups, setHiddenGroups] = useState(new Set());
  const normalizeChartType = (t) => {
    if (!t || typeof t !== "string") return "line";
    const v = t.trim();
    if (v === "donut") return "pie";
    if (
      v !== "line" &&
      v !== "bar" &&
      v !== "column" &&
      v !== "pie" &&
      v !== "table"
    )
      return "line";
    return v;
  };
  const [selectedChartType, setSelectedChartType] = useState(() =>
    normalizeChartType(type),
  );
  useEffect(() => {
    const next = normalizeChartType(type);
    setSelectedChartType((prev) => (prev === next ? prev : next));
  }, [type]);
  const effectiveType = selectedChartType;
  const dateCfg = {
    ...(baseConfig?.dateConfig || {}),
    ...((options && options.dateConfig) || {}),
  };
  const seriesFilterCfg = {
    ...(baseConfig?.seriesFilter || {}),
    ...((options && options.seriesFilter) || {}),
  };
  const toggleChartCfg = {
    ...(baseConfig?.toggleChart || {}),
    ...((options && options.toggleChart) || {}),
  };
  const legendCfg = {
    ...(baseConfig?.legend || {}),
    ...((options && options.legend) || {}),
  };
  const toggleChartShow =
    typeof toggleChartCfg?.showToggle === "boolean"
      ? toggleChartCfg.showToggle
      : true;
  const toggleMenuEffective = useMemo(() => {
    const allowRaw = Array.isArray(toggleChartCfg?.toggleList)
      ? toggleChartCfg.toggleList
      : [];
    const allow = allowRaw
      .map((v) => normalizeChartType(v))
      .filter((v) => typeof v === "string" && v);
    const typeNorm = normalizeChartType(type);
    if (allow.length === 0) return baseToggleMenu;
    const allowSet = new Set([...allow, typeNorm]);
    return baseToggleMenu.filter((it) => allowSet.has(it.value));
  }, [toggleChartCfg?.toggleList]);

  const toDateOnly = (v) => {
    if (!v) return null;
    if (typeof v === "string") {
      const s = v.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      const d = new Date(s);
      if (isNaN(d)) return null;
      const yyyy = String(d.getFullYear());
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
    if (v instanceof Date) {
      if (isNaN(v)) return null;
      const yyyy = String(v.getFullYear());
      const mm = String(v.getMonth() + 1).padStart(2, "0");
      const dd = String(v.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
    return null;
  };

  const normalizeRange = (range) => {
    if (!range || typeof range !== "object") return { from: null, to: null };
    return { from: toDateOnly(range.from), to: toDateOnly(range.to) };
  };

  const [selectedRange, setSelectedRange] = useState(
    normalizeRange(dateCfg && dateCfg.dateRange) || {
      from: new Date().toISOString().split("T")[0],
      to: new Date().toISOString().split("T")[0],
    },
  );
  const [tip, setTip] = useState({
    visible: false,
    x: 0,
    y: 0,
    title: "",
    rows: [],
  });
  useEffect(() => {
    setHidden(new Set());
    setTip({ visible: false, x: 0, y: 0, title: "", rows: [] });
  }, [effectiveType]);

  useEffect(() => {
    if (!dateCfg?.dateFilter) return;
    const next = dateCfg?.dateRange;
    if (!next) return;
    const { from, to } = normalizeRange(next);
    setSelectedRange((prev) => {
      const prevFrom = prev?.from ?? null;
      const prevTo = prev?.to ?? null;
      if (prevFrom === from && prevTo === to) return prev;
      return { from, to };
    });
  }, [dateCfg?.dateFilter, dateCfg?.dateRange?.from, dateCfg?.dateRange?.to]);

  useEffect(() => {
    if (!dateCfg?.dateFilter) return;
    if (typeof dateCfg?.onChange !== "function") return;
    const { from, to } = normalizeRange(selectedRange);
    if (!from && !to) return;
    if (
      toDateOnly(dateCfg?.dateRange?.from) === from &&
      toDateOnly(dateCfg?.dateRange?.to) === to
    )
      return;
    dateCfg.onChange({ from, to });
  }, [
    dateCfg?.dateFilter,
    dateCfg?.onChange,
    dateCfg?.dateRange?.from,
    dateCfg?.dateRange?.to,
    selectedRange?.from,
    selectedRange?.to,
  ]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((e) => {
      const r = e[0].contentRect;
      const topH = topSectionRef.current
        ? topSectionRef.current.getBoundingClientRect().height
        : 0;
      const bottomH = bottomLegendRef.current
        ? bottomLegendRef.current.getBoundingClientRect().height
        : 0;
      const measured = Math.max(0, Math.round(r.height - topH - bottomH));
      setDims({
        w: Math.round(r.width),
        h: isFullscreenOpen
          ? measured || 360
          : typeof height === "number"
            ? height
            : Math.round(r.height) || 360,
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [height, isFullscreenOpen]);
  const chartKind = useMemo(() => {
    if (effectiveType === "pie") return "pie";
    if (effectiveType === "bar") return "bar";
    if (effectiveType === "column") return "column";
    return "line";
  }, [effectiveType]);
  const mergedPadding = useMemo(() => {
    const basePad =
      baseConfig?.padding && typeof baseConfig.padding === "object"
        ? baseConfig.padding
        : {};
    const typePadBase =
      chartKind === "bar"
        ? barConfig?.padding
        : chartKind === "column"
          ? columnConfig?.padding
          : chartKind === "pie"
            ? pieConfig?.padding
            : lineConfig?.padding;
    const typePadOpt =
      chartKind === "bar"
        ? options?.bar?.padding
        : chartKind === "column"
          ? options?.column?.padding
          : chartKind === "pie"
            ? options?.pie?.padding
            : options?.line?.padding;
    const merged = {
      ...basePad,
      ...(typePadBase && typeof typePadBase === "object" ? typePadBase : {}),
      ...(typePadOpt && typeof typePadOpt === "object" ? typePadOpt : {}),
    };
    return padding || merged;
  }, [
    padding,
    chartKind,
    options?.bar,
    options?.column,
    options?.line,
    options?.pie,
  ]);
  const resolvedZeroValues = useMemo(() => {
    const base =
      chartKind === "bar"
        ? barConfig?.zeroValues
        : chartKind === "column"
          ? columnConfig?.zeroValues
          : null;
    const opt =
      chartKind === "bar"
        ? options?.bar?.zeroValues
        : chartKind === "column"
          ? options?.column?.zeroValues
          : null;
    return {
      ...(base && typeof base === "object" ? base : {}),
      ...(opt && typeof opt === "object" ? opt : {}),
    };
  }, [chartKind, options?.bar, options?.column]);
  const colors = useMemo(
    () => resolveColors(options.colors || baseConfig.colors),
    [options.colors],
  );
  const projectionColor = useMemo(
    () => resolveTokenColor(baseConfig.projection?.color || "gray-500"),
    [],
  );
  const parseDateValue = (v) => {
    if (!v) return null;
    if (v instanceof Date) return isNaN(v) ? null : v;
    if (typeof v === "string") {
      const d = new Date(v);
      return isNaN(d) ? null : d;
    }
    return null;
  };
  const indicesToKeep = useMemo(() => {
    if (!dateCfg?.dateFilter) return null;
    const from = parseDateValue(selectedRange?.from);
    const toRaw = parseDateValue(selectedRange?.to);
    const to = toRaw || from;
    if (!from || !to || !Array.isArray(categories) || categories.length === 0)
      return null;
    const fTime = new Date(
      from.getFullYear(),
      from.getMonth(),
      from.getDate(),
      0,
      0,
      0,
      0,
    ).getTime();
    const tTime = new Date(
      to.getFullYear(),
      to.getMonth(),
      to.getDate(),
      23,
      59,
      59,
      999,
    ).getTime();
    const anyParsable = categories.some((c) => !!parseDateValue(c));
    if (!anyParsable) return null;
    return categories
      .map((c, i) => {
        const cd = parseDateValue(c);
        if (!cd) return null;
        const ct = cd.getTime();
        return ct >= fTime && ct <= tTime ? i : null;
      })
      .filter((i) => i != null);
  }, [categories, selectedRange, dateCfg?.dateFilter]);
  const activeCategories = useMemo(() => {
    if (!indicesToKeep) return categories;
    return indicesToKeep.map((i) => categories[i]);
  }, [categories, indicesToKeep]);
  const activeData = useMemo(() => {
    if (!indicesToKeep || !Array.isArray(data)) return data;
    if (isGroupedSeries(data)) {
      return data.map((g) => {
        const seriesData = Array.isArray(g?.seriesData) ? g.seriesData : [];
        return {
          ...g,
          seriesData: seriesData.map((it) => {
            const arr = Array.isArray(it?.data) ? it.data : [];
            const filtered = indicesToKeep.map((i) => arr[i]);
            return { ...it, data: filtered };
          }),
        };
      });
    }
    return data.map((s) => {
      const arr = Array.isArray(s?.data) ? s.data : [];
      const filtered = indicesToKeep.map((i) => arr[i]);
      return { ...s, data: filtered };
    });
  }, [data, indicesToKeep]);
  const seriesNameKey =
    typeof seriesFilterCfg?.seriesNameKey === "string" &&
    seriesFilterCfg.seriesNameKey.trim()
      ? seriesFilterCfg.seriesNameKey.trim()
      : "seriesName";
  const seriesFilterShowSearch =
    typeof seriesFilterCfg?.showSearch === "boolean"
      ? seriesFilterCfg.showSearch
      : true;
  const seriesFilterSearchPlaceholder =
    typeof seriesFilterCfg?.searchPlaceholder === "string" &&
    seriesFilterCfg.searchPlaceholder.trim()
      ? seriesFilterCfg.searchPlaceholder
      : "Search";
  const seriesFilterEnabled =
    !!seriesFilterCfg?.showFilter && isGroupedSeries(activeData);
  const seriesGroupKey = useMemo(
    () => (g, idx) => {
      const id = g?.seriesId;
      if (id != null && String(id).trim() !== "") return `id:${String(id)}`;
      const nm = g?.[seriesNameKey];
      if (nm != null && String(nm).trim() !== "") return `name:${String(nm)}`;
      return `idx:${idx}`;
    },
    [seriesNameKey],
  );
  const seriesFilterOptions = useMemo(() => {
    if (!seriesFilterEnabled) return [];
    return activeData.map((g, idx) => {
      const labelRaw = g?.[seriesNameKey];
      const label =
        labelRaw != null && String(labelRaw).trim()
          ? String(labelRaw)
          : `Series ${idx + 1}`;
      return { label, value: seriesGroupKey(g, idx) };
    });
  }, [activeData, seriesFilterEnabled, seriesNameKey, seriesGroupKey]);
  const seriesFilterValue = useMemo(() => {
    if (!seriesFilterEnabled) return [];
    return seriesFilterOptions
      .map((o) => o.value)
      .filter((v) => !hiddenGroups.has(v));
  }, [hiddenGroups, seriesFilterEnabled, seriesFilterOptions]);
  useEffect(() => {
    if (!seriesFilterEnabled) return;
    setHiddenGroups(new Set());
  }, [data, seriesFilterEnabled, seriesNameKey]);
  const activeDataWithSeriesFilter = useMemo(() => {
    if (!seriesFilterEnabled) return activeData;
    if (!(hiddenGroups instanceof Set) || hiddenGroups.size === 0)
      return activeData;
    return activeData.filter(
      (g, idx) => !hiddenGroups.has(seriesGroupKey(g, idx)),
    );
  }, [activeData, hiddenGroups, seriesFilterEnabled, seriesGroupKey]);
  const activeProjection = useMemo(() => {
    if (!indicesToKeep || !Array.isArray(projection)) return projection;
    return indicesToKeep.map((i) => projection[i]);
  }, [projection, indicesToKeep]);
  const normalized = useMemo(() => {
    const groupedDefaults =
      effectiveType === "table"
        ? tableConfig?.grouped
        : chartKind === "line"
          ? lineConfig?.groupConfig
          : chartKind === "bar"
            ? barConfig?.grouped
            : chartKind === "column"
              ? columnConfig?.grouped
              : chartKind === "pie"
                ? pieConfig?.pie?.groupConfig
                : undefined;
    const mergedGrouped =
      groupedDefaults || (options && options.grouped)
        ? {
            ...(groupedDefaults || {}),
            ...((options && options.grouped) || {}),
          }
        : undefined;
    const mergedOptions = mergedGrouped
      ? { ...(options || {}), grouped: mergedGrouped }
      : options;
    return normalizeForChart({
      chartType: chartKind,
      inputSeries: activeDataWithSeriesFilter,
      categories: activeCategories,
      options: mergedOptions,
    });
  }, [
    effectiveType,
    chartKind,
    activeDataWithSeriesFilter,
    activeCategories,
    options,
  ]);
  const zeroMode = resolvedZeroValues?.mode;
  const normalizedForRender = normalized;
  const projectionForRender = activeProjection;
  const colorMap = useMemo(() => {
    const base = normalizedForRender.series.map(
      (_, i) => colors[i % colors.length] || "#3b82f6",
    );
    if (
      Array.isArray(projectionForRender) &&
      projectionForRender.length > 0
    ) {
      base.push(projectionColor);
    }
    return base;
  }, [normalizedForRender.series, colors, projectionForRender, projectionColor]);
  const stackedForGrouped =
    chartKind === "bar"
      ? typeof options?.bar?.stacked === "boolean"
        ? options.bar.stacked
        : false
      : chartKind === "column"
        ? typeof options?.column?.stacked === "boolean"
          ? options.column.stacked
          : typeof options?.bar?.stacked === "boolean"
            ? options.bar.stacked
            : false
        : false;
  const groupedStacked =
    (chartKind === "bar" || chartKind === "column") &&
    !!stackedForGrouped &&
    normalizedForRender.series.some((s) => typeof s?.__group === "number");
  const groupedByName =
    groupedStacked ||
    (effectiveType === "table" &&
      normalizedForRender.series.some((s) => typeof s?.__group === "number"));
  const groupedColorIndexByName = useMemo(() => {
    if (!groupedByName) return new Map();
    const map = new Map();
    normalizedForRender.series.forEach((s) => {
      const nm = typeof s?.name === "string" ? s.name : "";
      if (!nm || map.has(nm)) return;
      map.set(nm, map.size);
    });
    return map;
  }, [groupedByName, normalizedForRender.series]);
  const projectionSeries = useMemo(() => {
    if (
      Array.isArray(projectionForRender) &&
      projectionForRender.length > 0
    ) {
      return {
        data: projectionForRender,
        __idx: normalizedForRender.series.length,
        __projection: true,
      };
    }
    return null;
  }, [projectionForRender, normalizedForRender.series.length]);
  const axisLabelFontSizePx =
    typeof baseConfig.axis?.fontSize === "number"
      ? baseConfig.axis.fontSize
      : 11;
  const axisLabelColor = resolveTokenColor(baseConfig.axis.labelColor);
  const axisYWidthUsed = (() => {
    const baseW =
      typeof baseConfig.axis?.y?.width === "number"
        ? Math.max(0, baseConfig.axis.y.width)
        : 0;
    const globalOptW =
      typeof options?.axis?.y?.width === "number"
        ? Math.max(0, options.axis.y.width)
        : null;
    const typeBaseW =
      chartKind === "bar"
        ? typeof barConfig?.axis?.y?.width === "number"
          ? Math.max(0, barConfig.axis.y.width)
          : null
        : chartKind === "column"
          ? typeof columnConfig?.axis?.y?.width === "number"
            ? Math.max(0, columnConfig.axis.y.width)
            : null
          : null;
    const typeOptW =
      chartKind === "bar"
        ? typeof options?.bar?.axis?.y?.width === "number"
          ? Math.max(0, options.bar.axis.y.width)
          : null
        : chartKind === "column"
          ? typeof options?.column?.axis?.y?.width === "number"
            ? Math.max(0, options.column.axis.y.width)
            : null
          : null;
    return typeOptW ?? globalOptW ?? typeBaseW ?? baseW;
  })();
  const yTitleText = (
    options.yLabelTitle ??
    baseConfig.axis?.y?.labelTitle ??
    ""
  ).trim();
  const xTitleText = (
    options.xLabelTitle ??
    baseConfig.axis?.x?.labelTitle ??
    baseConfig.axis?.labelTitle ??
    ""
  ).trim();
  const yLabelW = yTitleText ? 20 : 0;
  const xLabelH = xTitleText ? 20 : 0;
  const formatDataValue = (v) => {
    const cfg = {
      ...(baseConfig.data || {}),
      ...((options && options.data) || {}),
    };
    const pre = cfg.prefix || "";
    const suf = cfg.suffix || "";
    const fmt = cfg.format;
    let out = v;
    if (fmt === "compact") {
      try {
        out = Intl.NumberFormat(undefined, {
          notation: "compact",
          maximumFractionDigits: 1,
        }).format(v);
      } catch {
        out = String(v);
      }
    } else if (fmt === "commas") {
      try {
        out = Intl.NumberFormat(undefined).format(v);
      } catch {
        out = String(v);
      }
    } else if (fmt === "raw") {
      out = String(v);
    } else {
      try {
        out = Intl.NumberFormat(undefined).format(v);
      } catch {
        out = String(v);
      }
    }
    return `${pre}${out}${suf}`;
  };
  const drawnData = useMemo(() => {
    const base = normalizedForRender.series
      .map((s, i) => {
        const next = { ...s, __idx: i };
        if (groupedByName) {
          const nm = typeof s?.name === "string" ? s.name : "";
          const ci = nm ? groupedColorIndexByName.get(nm) : undefined;
          if (typeof ci === "number" && isFinite(ci)) next.__colorIdx = ci;
        }
        return next;
      })
      .filter((s) =>
        groupedByName
          ? !(typeof s?.name === "string" && hidden.has(s.name))
          : !hidden.has(s.__idx),
      );
    const projectionEnabled = !!(
      projectionSeries && baseConfig.projection?.show !== false
    );
    if (projectionEnabled) base.push(projectionSeries);
    return base;
  }, [
    normalizedForRender.series,
    hidden,
    projectionSeries,
    groupedByName,
    groupedColorIndexByName,
  ]);
  const handleHover = (ev) => {
    if (!ref.current) return;
    if (!ev) {
      setTip({ visible: false, x: 0, y: 0, title: "", rows: [] });
      return;
    }
    const rect = ref.current.getBoundingClientRect();
    const tipWidth = 160;
    const prefX = Math.round(ev.guidelineX - rect.left + 12);
    const x =
      prefX + tipWidth > rect.width
        ? Math.round(ev.guidelineX - rect.left - tipWidth - 12)
        : prefX;
    const y = Math.round(ev.topY - rect.top);
    const isRowDriven = Array.isArray(ev.rows) && ev.rows.length > 0;
    const seriesForTooltip = drawnData.filter((s) => !s.__projection);
    let rows = isRowDriven
      ? ev.rows
      : seriesForTooltip.map((s) => ({
          name: s.name || `Series ${(s.__idx ?? 0) + 1}`,
          color: colorMap[s.__idx],
          value: formatDataValue(s.data[ev.index]),
        }));
    if (
      (effectiveType === "bar" || effectiveType === "column") &&
      zeroMode === "hide"
    ) {
      if (isRowDriven && Array.isArray(ev.values) && ev.values.length > 0) {
        rows = rows.filter((_, i) => {
          const vRaw = ev.values[i];
          const v = vRaw != null && isFinite(vRaw) ? Number(vRaw) : 0;
          return v !== 0;
        });
      } else {
        rows = seriesForTooltip
          .map((s) => {
            const vRaw = s?.data?.[ev.index];
            const v = vRaw != null && isFinite(vRaw) ? Number(vRaw) : 0;
            if (v === 0) return null;
            return {
              name: s.name || `Series ${(s.__idx ?? 0) + 1}`,
              color: colorMap[s.__idx],
              value: formatDataValue(vRaw),
            };
          })
          .filter(Boolean);
      }
      if (!rows || rows.length === 0) {
        setTip({ visible: false, x: 0, y: 0, title: "", rows: [] });
        return;
      }
    }
    const title =
      (typeof ev.category === "string" && ev.category) ||
      normalizedForRender.categories[ev.index];
    setTip({ visible: true, x, y, title, rows });
  };
  const legendShow = !!legendCfg?.show;
  const toggleLegend = (i) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  const columnStacked =
    typeof options?.column?.stacked === "boolean"
      ? options.column.stacked
      : typeof options?.bar?.stacked === "boolean"
        ? options.bar.stacked
        : undefined;
  const barStacked =
    typeof options?.bar?.stacked === "boolean"
      ? options.bar.stacked
      : undefined;
  const edgePadding =
    options && options.axis
      ? (options.axis.edgePadding ?? options.axis.dgePadding)
      : undefined;
  const pieLabelsAll = chartKind === "pie" ? normalizedForRender.categories : [];
  const pieValuesAll =
    chartKind === "pie" && normalizedForRender.series?.[0]?.data
      ? normalizedForRender.series[0].data
      : [];
  const pieVisibleIdx =
    chartKind === "pie"
      ? pieLabelsAll.map((_, i) => i).filter((i) => !hidden.has(i))
      : [];
  const pieLabels =
    chartKind === "pie" ? pieVisibleIdx.map((i) => pieLabelsAll[i]) : [];
  const pieValues =
    chartKind === "pie"
      ? pieVisibleIdx.map((i) => {
          const v = pieValuesAll[i];
          return v != null && isFinite(v) ? Number(v) : 0;
        })
      : [];
  const pieSeries =
    chartKind === "pie" ? [{ name: "data", data: pieValues }] : [];
  const pieColorMap =
    chartKind === "pie"
      ? pieVisibleIdx.map((i) => colors[i % colors.length] || "#3b82f6")
      : [];
  const legendItems = useMemo(() => {
    if (chartKind === "pie") {
      return pieLabelsAll.map((label, i) => ({
        index: i,
        label: label || `Slice ${i + 1}`,
        color: colors[i % colors.length] || "#3b82f6",
      }));
    }
    if (groupedByName) {
      const items = [];
      const seen = new Set();
      normalizedForRender.series.forEach((s) => {
        const nm = typeof s?.name === "string" ? s.name : "";
        if (!nm || seen.has(nm)) return;
        const idx = groupedColorIndexByName.get(nm) ?? items.length;
        items.push({
          index: nm,
          label: nm,
          color: colors[idx % colors.length] || "#3b82f6",
        });
        seen.add(nm);
      });
      return items;
    }
    return normalizedForRender.series.map((s, i) => ({
      index: i,
      label: s.name || `Series ${i + 1}`,
      color: colorMap[i],
    }));
  }, [
    chartKind,
    pieLabelsAll,
    normalizedForRender.series,
    colorMap,
    colors,
    groupedByName,
    groupedColorIndexByName,
  ]);

  const chartBody = (
    <div
      ref={ref}
      style={{
        width: isFullscreenOpen ? "100%" : width,
        height: isFullscreenOpen ? "100%" : undefined,
        position: "relative",
        background: resolveTokenColor(baseConfig.background),
      }}
      className={`p-5 flex flex-col ${
        isFullscreenOpen
          ? "shadow-none rounded-none h-full flex-1 min-h-0"
          : "shadow-xl rounded-md"
      }`}
    >
      <div ref={topSectionRef}>
        <div className="p-1 flex justify-between items-center">
          {title && <div className="font-medium">{title}</div>}
          <div className="toolBar flex flex-wrap items-center justify-end gap-2">
            {dateCfg.dateFilter && (
              <div style={{ minWidth: 260 }}>
                <FormFieldSet
                  title="Select date"
                  noTitle
                  size="sm"
                  type="datepickerRange"
                  placeholder="Select date"
                  value={selectedRange}
                  maxDate={new Date()}
                  onChange={(v) => setSelectedRange(normalizeRange(v))}
                  defaultOption={dateCfg.defaultOption}
                />
              </div>
            )}
            {seriesFilterEnabled && seriesFilterOptions.length > 0 && (
              <Select
                options={seriesFilterOptions}
                value={seriesFilterValue}
                onChange={(vals) =>
                  setHiddenGroups(() => {
                    const all = seriesFilterOptions.map((o) => o.value);
                    const keep = new Set(vals);
                    const next = new Set();
                    for (const v of all) {
                      if (!keep.has(v)) next.add(v);
                    }
                    return next;
                  })
                }
                placeholder={seriesFilterCfg?.title || "Series"}
                maxHeight={160}
                showSwatch={false}
                showSearch={seriesFilterShowSearch}
                searchPlaceholder={seriesFilterSearchPlaceholder}
              />
            )}

            {toggleChartShow && toggleMenuEffective.length > 0 && (
              <DropdownMenuNew
                MenuSection={({ closeDropdown }) => (
                  <div className="flex flex-col bg-gray-50">
                    {toggleMenuEffective.map((it, index) => (
                      <div key={it.title} className="w-full">
                        <Button
                          title={it.title}
                          icon={it.icon}
                          menuAlign="right"
                          size="sm"
                          variant="text"
                          color={
                            effectiveType === it.value ? "primary" : "black"
                          }
                          className="p-2! justify-start w-full"
                          onClick={() => {
                            setSelectedChartType(it.value);
                            closeDropdown();
                          }}
                        />
                        {index !== toggleMenuEffective.length - 1 && (
                          <Divider />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                minWidth={"90px"}
                maxWidth={"100px"}
              >
                <Button
                  title={"Menu"}
                  type="icon"
                  icon={"chart_data"}
                  menuAlign="right"
                  color="black"
                  size="md"
                  className="rounded! w-8 h-8 border-black/20 "
                  variant="outlined"
                />
              </DropdownMenuNew>
            )}

            <Button
              title={!isFullscreenOpen ? "Fullscreen" : "Exit Fullscreen"}
              type="icon"
              icon={!isFullscreenOpen ? "fullscreen" : "fullscreen_exit"}
              menuAlign="right"
              color="black"
              size="md"
              className="rounded! w-8 h-8 border-black/20 "
              variant="outlined"
              onClick={() => setIsFullscreenOpen((s) => !s)}
            />
          </div>
        </div>

        {legendShow &&
          legendItems.length > 0 &&
          legendCfg?.mode === "select" && (
            <div className="mb-2 flex justify-end">
              <Select
                options={legendItems.map((it) => ({
                  label: it.label,
                  value: it.index,
                }))}
                value={legendItems
                  .map((it) => it.index)
                  .filter((i) => !hidden.has(i))}
                onChange={(vals) =>
                  setHidden(() => {
                    const all = legendItems.map((it) => it.index);
                    const keep = new Set(vals);
                    const next = new Set();
                    for (const idx of all) {
                      if (!keep.has(idx)) next.add(idx);
                    }
                    return next;
                  })
                }
                placeholder="Legend"
                maxHeight={
                  typeof legendCfg?.maxHeight === "number"
                    ? legendCfg.maxHeight
                    : 160
                }
                colors={Object.fromEntries(
                  legendItems.map((it) => [it.index, it.color]),
                )}
                showSwatch={!groupedStacked}
                showSearch={!!legendCfg?.showSearch}
                searchPlaceholder={legendCfg?.searchPlaceholder || "Search"}
              />
            </div>
          )}
      </div>

      <div
        className={`grid gap-0 ${isFullscreenOpen ? "flex-1 min-h-0" : ""}`}
        style={{
          gridTemplateColumns: `${yLabelW}px repeat(4,1fr)`,
          gridTemplateRows: `repeat(4,1fr) ${xLabelH}px`,
        }}
      >
        {yLabelW > 0 && (
          <div className="row-span-5 flex items-center justify-center ">
            <span
              style={{
                transform: "rotate(-90deg)",
                transformOrigin: "center",
                fontSize: `${axisLabelFontSizePx}px`,
                color: axisLabelColor,
                whiteSpace: "nowrap",
              }}
            >
              {yTitleText}
            </span>
          </div>
        )}
        <div className="row-span-4">
          {effectiveType === "line" && (
            <>
              <LineChartsNew
                series={drawnData}
                categories={normalizedForRender.categories}
                width={Math.max(0, (dims.w || 600) - yLabelW)}
                height={Math.max(0, (dims.h || 360) - xLabelH)}
                colors={colors}
                curve={options.curve}
                strokeWidth={options.strokeWidth}
                markersSize={options.markersSize}
                dataLabelsEnabled={options.dataLabelsEnabled}
                areaShow={options.area?.show}
                areaFill={options.area?.fill}
                edgePadding={edgePadding}
                padding={mergedPadding}
                title={options.chartTitle}
                xLabelTitle={""}
                yLabelTitle={""}
                colorMap={colorMap}
                componentId={componentId}
                onHover={handleHover}
              />
              <Tooltip
                visible={tip.visible}
                x={tip.x}
                y={tip.y}
                title={tip.title}
                rows={tip.rows}
              />
            </>
          )}
          {effectiveType === "column" && (
            <>
              <ColumnChartNew
                series={drawnData}
                categories={normalizedForRender.categories}
                width={Math.max(0, (dims.w || 600) - yLabelW - axisYWidthUsed)}
                height={Math.max(0, (dims.h || 360) - xLabelH)}
                colors={colors}
                dataLabelsEnabled={options.dataLabelsEnabled}
                stacked={columnStacked}
                columnOptions={options.column}
                edgePadding={edgePadding}
                padding={mergedPadding}
                zeroValues={resolvedZeroValues}
                axisYWidth={axisYWidthUsed}
                title={options.chartTitle}
                xLabelTitle={""}
                yLabelTitle={""}
                colorMap={colorMap}
                componentId={componentId}
                onHover={handleHover}
              />
              <Tooltip
                visible={tip.visible}
                x={tip.x}
                y={tip.y}
                title={tip.title}
                rows={tip.rows}
              />
            </>
          )}
          {effectiveType === "bar" && (
            <>
              <BarChartNew
                series={drawnData}
                categories={normalizedForRender.categories}
                width={Math.max(0, (dims.w || 600) - yLabelW)}
                height={Math.max(0, (dims.h || 360) - xLabelH)}
                colors={colors}
                dataLabelsEnabled={options.dataLabelsEnabled}
                stacked={barStacked}
                barOptions={options.bar}
                edgePadding={edgePadding}
                padding={mergedPadding}
                zeroValues={resolvedZeroValues}
                axisYWidth={axisYWidthUsed}
                title={options.chartTitle}
                xLabelTitle={""}
                yLabelTitle={""}
                colorMap={colorMap}
                componentId={componentId}
                onHover={handleHover}
              />
              <Tooltip
                visible={tip.visible}
                x={tip.x}
                y={tip.y}
                title={tip.title}
                rows={tip.rows}
              />
            </>
          )}
          {effectiveType === "pie" && (
            <>
              <PieChartNew
                series={pieSeries}
                categories={pieLabels}
                width={Math.max(0, dims.w || 600)}
                height={Math.max(0, dims.h || 360)}
                colors={colors}
                dataLabelsEnabled={options.dataLabelsEnabled}
                padding={mergedPadding}
                title={options.chartTitle}
                colorMap={pieColorMap}
                pieOptions={options.pie}
                componentId={componentId}
                onHover={handleHover}
              />
              <Tooltip
                visible={tip.visible}
                x={tip.x}
                y={tip.y}
                title={tip.title}
                rows={tip.rows}
              />
            </>
          )}
          {effectiveType === "table" && (
            <>
              <TableChartNew
                series={drawnData.filter((s) => !s.__projection)}
                categories={normalizedForRender.categories}
                width={Math.max(0, dims.w || 600)}
                height={Math.max(0, dims.h || 360)}
                componentId={componentId}
                tableConfig={options?.table}
              />
            </>
          )}
        </div>
        {xLabelH > 0 && (
          <div className="col-span-4 col-start-2 row-start-5 flex items-center justify-center">
            <span
              style={{
                fontSize: `${axisLabelFontSizePx}px`,
                color: axisLabelColor,
                whiteSpace: "nowrap",
              }}
            >
              {xTitleText}
            </span>
          </div>
        )}
      </div>

      <div ref={bottomLegendRef}>
        {legendShow &&
          legendItems.length > 0 &&
          legendCfg?.mode !== "select" && (
            <div className="mt-2 flex flex-wrap gap-4 justify-center">
              {legendItems.map((it) => {
                const c = it.color;
                const isHidden = hidden.has(it.index);
                return (
                  <div
                    key={it.index}
                    className="flex items-center gap-1"
                    onClick={() => toggleLegend(it.index)}
                    style={{ cursor: "pointer", opacity: isHidden ? 0.6 : 1 }}
                  >
                    <span
                      style={{
                        background: isHidden ? "transparent" : c,
                        border: `1px solid ${c}`,
                      }}
                      className="w-2 h-2 rounded-full inline-block"
                    />
                    <span className="text-xs text-gray-600">{it.label}</span>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );

  return (
    <div className="flex-1 w-full flex flex-col gap-1">
      {!isFullscreenOpen && chartBody}

      <Modal
        showCloseButton={false}
        isOpen={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
        width="max-w-[100vw]"
        wrapperClassName="p-0!"
        panelClassName="!max-h-[100vh] h-[100dvh] rounded-none!"
        bodyClassName="p-0! flex flex-1 overflow-visible min-h-0"
        closeOnOutsideClick
        zIndex={48}
      >
        <div className="w-full overflow-visible flex flex-col flex-1 min-h-0 px-0 pt-2 pb-6">
          {chartBody}
        </div>
      </Modal>
    </div>
  );
};

export default ChartsSetWrapperNew;
